import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useClientList } from '../api/queries.ts';
import { useMyDashboards } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import type { ClientListRow } from '../api/types.ts';

const PAGE_SIZE = 25;

const SORTS: { value: string; label: string }[] = [
  { value: 'last_name', label: 'Name' },
  { value: 'client_ref', label: 'Reference' },
  { value: 'status', label: 'Status' },
  { value: 'where', label: 'Where they are' },
  { value: 'case_manager', label: 'Case manager' },
];

/**
 * Looking someone up.
 *
 * The most common thing this application will ever do, and the one most likely
 * to be done standing up, one-handed, in a lobby, on somebody else's wifi. So:
 *
 *  - The search box is the first thing, focused, and large enough to type into
 *    without aiming. Everything else on the screen is secondary to it.
 *  - Rows are whole tap targets — a `Link` wrapping the row, not a link on the
 *    name — with a 72px minimum, because the thing being tapped is a person's
 *    record and the cost of hitting the wrong one is opening the wrong client
 *    in front of them.
 *  - The previous results stay on screen while the next query runs. A list
 *    that empties on every keystroke cannot be typed into on a slow
 *    connection, and blanking is the failure this codebase has already made
 *    once, on the client header.
 *  - A Critical flag is marked here, not only on the header. By the time the
 *    header renders, the row has already been chosen; a case manager scanning
 *    for the right person should see which records carry a warning before they
 *    commit to one.
 *
 * Search matches aliases as well as names, which is the whole reason
 * `client_alias` exists — the name in the system is often not the name in the
 * searcher's head. Typing "Fairholm" finds the five clients for whom it is a
 * maiden name and who are recorded under married names.
 */
export function ClientList() {
  // In the URL, so a search survives a reload and can be handed to someone.
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sort = params.get('sort') ?? 'last_name';
  const offset = Number(params.get('offset') ?? 0);

  // Typing updates the box immediately and the query a beat later. 250ms is
  // long enough to not query per keystroke and short enough not to feel like
  // the screen is ignoring you.
  const [typed, setTyped] = useState(q);
  useEffect(() => {
    if (typed === q) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (typed) next.set('q', typed); else next.delete('q');
      next.delete('offset');
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, q, params, setParams]);

  const list = useClientList({ q, sort, offset, limit: PAGE_SIZE });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key); else next.set(key, value);
    }
    setParams(next);
  };

  /**
   * What the rows on screen are allowed to claim.
   *
   * Rows render only when they are an answer: this query's, or the last good
   * one while a refresh fails on bad wifi. Two things are not answers:
   *
   *  - Anything after a session failure. A 401, or a 403 from the
   *    authenticator, takes the screen even with rows in hand, because the
   *    server has just said this person may not be shown them.
   *  - Placeholder rows once the request they stand in for has failed. They are
   *    the previous search, kept so typing does not blank the list; but a
   *    failing search for "zzq" otherwise sat under the previous search's 25
   *    rows and "1–25 of 840" for as long as the retries lasted — seen, with
   *    retries paused in a background tab, indefinitely.
   *
   * And the empty state is only for a successful response with zero rows.
   */
  const failure = list.error ?? (list.isPlaceholderData ? list.failureReason : null);
  const takeover =
    failure !== null && (isSessionFailure(failure) || !list.data || list.isPlaceholderData);

  const rows = takeover ? [] : list.data?.clients ?? [];
  const total = takeover ? 0 : list.data?.total ?? 0;
  const showing = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="min-h-dvh bg-neutral-50">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
      <header className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 pt-3 pb-2
                         lg:static lg:rounded-t-lg lg:border-b-0 lg:px-5 lg:pt-4 lg:shadow">
        {/*
          Stacked on a tablet, one row from lg. The search box is capped rather
          than stretched: a 1500px text input is harder to use than a 400px one,
          and the width is better spent on the rows below.
        */}
        <div className="lg:flex lg:items-center lg:gap-4">
          <h1 className="text-xl font-bold text-neutral-900 lg:shrink-0">Clients</h1>

          <label className="sr-only" htmlFor="client-search">
            Search by name, reference or former name
          </label>
          <input
            id="client-search"
            type="search"
            inputMode="search"
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Name, reference, or a former name"
            className="mt-2 min-h-[52px] w-full rounded-lg border border-neutral-300 px-3
                       text-base text-neutral-900 lg:mt-0 lg:min-h-[44px] lg:max-w-[26rem]"
          />

          <div className="mt-2 flex items-center gap-2 lg:mt-0 lg:ml-auto">
            {/*
              Adding a client is the only action on this screen that creates
              something, so it reads as an action rather than as another link:
              the list is for finding people, and this is for the one case where
              the person is not there to be found.

              Shown to everyone. Who may actually create is scope, decided by
              the endpoint and by row security underneath it — but a caseload
              role that taps this gets a refusal in words, which is better than
              a control that silently is not there when somebody has been told
              to use it.
            */}
            <Link
              to="/clients/new"
              className="inline-flex min-h-[44px] items-center rounded-lg border
                         border-neutral-300 px-3 text-[0.9375rem] font-semibold
                         text-neutral-800 hover:bg-neutral-50"
            >
              Add a client
            </Link>
            <Link
              to="/work"
              className="inline-flex min-h-[44px] items-center rounded-lg px-2 text-[0.9375rem]
                         font-semibold text-neutral-600 hover:text-neutral-900"
            >
              Tasks due
            </Link>
            <DashboardsLink />
            <label className="sr-only" htmlFor="client-sort">Sort by</label>
            <select
              id="client-sort"
              value={sort}
              onChange={(e) => update({ sort: e.target.value, offset: null })}
              className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2
                         text-[0.9375rem] text-neutral-800"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="ml-auto text-sm text-neutral-500 tabular-nums lg:ml-0">
              {takeover ? 'Not loaded'
                : list.isPending || list.isPlaceholderData ? 'Searching…'
                : total === 0 ? (q ? 'No matches' : 'No clients')
                : `${showing}–${to} of ${total}`}
            </p>
          </div>
        </div>
      </header>

      <div className="bg-white lg:rounded-b-lg lg:shadow">
      {/* Column labels exist only where there are columns. */}
      <div className={`hidden border-b border-neutral-200 px-4 py-2 text-xs font-bold
                       tracking-[0.12em] text-neutral-500 uppercase lg:px-5 ${COLUMNS} lg:!grid`}>
        <span>Name</span>
        <span>Ref</span>
        <span>Where they are</span>
        <span>Case manager</span>
        <span className="text-right">Status</span>
      </div>

      {/* One message per cause: the failure panel says it, so Staleness does not. */}
      {!takeover && (
        <Staleness
          updatedAt={list.dataUpdatedAt}
          isFetching={list.isFetching}
          error={list.error}
          onRefresh={() => void list.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="The client list"
          error={failure}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : list.isSuccess && !list.isPlaceholderData && rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-neutral-500">
          {q
            ? `Nothing matches “${q}”. Former names are searched too.`
            : 'No clients.'}
        </p>
      ) : (
        <ol className="divide-y divide-neutral-200">
          {rows.map((client) => <Row key={client.client_id} client={client} />)}
        </ol>
      )}

      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center gap-3 px-4 py-4 lg:max-w-md lg:px-0 lg:pt-4">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => update({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
            className="min-h-[48px] flex-1 rounded-lg border border-neutral-300 font-semibold
                       text-neutral-700 active:bg-neutral-100 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={to >= total}
            onClick={() => update({ offset: String(offset + PAGE_SIZE) })}
            className="min-h-[48px] flex-1 rounded-lg border border-neutral-300 font-semibold
                       text-neutral-700 active:bg-neutral-100 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * Shown only to someone who holds a dashboard, which is decided from every
 * role they hold. While that is unknown, or if asking failed, there is simply
 * no link: its absence claims nothing about the data.
 */
function DashboardsLink() {
  const mine = useMyDashboards();
  if (!mine.data || mine.data.dashboards.length === 0) return null;
  return (
    <Link
      to="/dashboards"
      className="inline-flex min-h-[44px] items-center rounded-lg px-2 text-[0.9375rem]
                 font-semibold text-neutral-600 hover:text-neutral-900"
    >
      Dashboards
    </Link>
  );
}

/** The column widths, written once so the header row and the data rows cannot
 *  drift apart. Only applies from `lg`; below that the row is a stacked card. */
const COLUMNS =
  'lg:grid lg:grid-cols-[minmax(0,24rem)_7rem_minmax(0,1fr)_minmax(0,13rem)_9rem] lg:items-center lg:gap-4';

function CriticalMark({ count }: { count: number }) {
  // Not a count alone and not colour alone — the same rule as the header, one
  // screen earlier. It says what it is, in words, with a shape.
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-critical-bg px-2 py-0.5
                     text-xs font-bold tracking-wide text-critical-fg uppercase">
      <svg viewBox="0 0 24 24" aria-hidden="true" className="size-3.5 shrink-0 fill-current">
        <path d="M12 2 1 21h22L12 2Zm0 6 1 7h-2l1-7Zm0 9.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Z" />
      </svg>
      Critical{count > 1 && ` · ${count}`}
    </span>
  );
}

function Row({ client }: { client: ClientListRow }) {
  const where = client.whereabouts_unknown
    ? 'Whereabouts unknown'
    : client.where_to_find_them ?? 'Not recorded';

  return (
    <li data-quick-client={client.client_id}>
      <Link
        to={`/clients/${client.client_id}`}
        state={{ from: 'Clients' }}
        // 72px on touch, 52px from lg — denser for scanning with a mouse and
        // still well above the 44px floor, which costs a pointer nothing.
        className={`block min-h-[72px] px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100
                    lg:min-h-[52px] lg:py-2.5 ${COLUMNS}`}
      >
        {/* name — and the ref beside it below lg, where there is no ref column */}
        <div className="flex min-w-0 items-baseline gap-2">
          <p className="min-w-0 truncate font-semibold text-neutral-900">
            {client.display_name}
          </p>
          <span className="ml-auto shrink-0 font-mono text-sm text-neutral-500 tabular-nums lg:hidden">
            {client.client_ref}
          </span>
        </div>

        <p className="hidden font-mono text-sm text-neutral-500 tabular-nums lg:block">
          {client.client_ref}
        </p>

        <p className="mt-0.5 truncate text-[0.9375rem] text-neutral-600 lg:mt-0">
          <span className={client.whereabouts_unknown ? 'font-semibold text-critical-edge' : undefined}>
            {where}
          </span>
        </p>

        {/* Below lg these two fold into one line; from lg they are columns. */}
        <p className="mt-0.5 truncate text-sm text-neutral-500 lg:mt-0 lg:text-[0.9375rem]">
          <span className="lg:hidden">
            {[client.status, client.case_manager ?? 'No case manager'].filter(Boolean).join(' · ')}
          </span>
          <span className="hidden lg:inline">
            {client.case_manager ?? 'No case manager'}
          </span>
        </p>

        <div className="mt-1 flex items-center gap-2 lg:mt-0 lg:justify-end">
          <span className="hidden truncate text-sm text-neutral-500 lg:inline">
            {client.critical_flags > 0 ? '' : client.status}
          </span>
          {client.critical_flags > 0 && <CriticalMark count={client.critical_flags} />}
        </div>
      </Link>
    </li>
  );
}
